import React, { useCallback, useEffect, useMemo, useState } from "react"
import { CardBody, Spinner, Button, Input, Alert } from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { getModernTableStyles } from "../Tables/modernTableStyles"
import { getOnlineUsers } from "../../api/requests/online-users-requests"
import { translateRole } from "../../constants/roleDisplay"

const NAVY = "#221C5D"
const GREEN = "#71C30B"
const ORANGE = "#E56A22"
const REFRESH_MS = 30000

const TableCard = ({ className = "", children }) => (
  <div className={`card rounded-4 border-0 shadow-lg ${className}`} style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}>
    {children}
  </div>
)

const browserOf = (ua, clientType) => {
  if (clientType === "MOBILE") return "Mobil uygulama"
  if (!ua) return "-"
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\//.test(ua) ? "Opera"
    : /Chrome\//.test(ua) ? "Chrome"
    : /Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari"
    : "Tarayıcı"
  const os = /Windows/.test(ua) ? "Windows"
    : /iPhone|iPad/.test(ua) ? "iOS"
    : /Android/.test(ua) ? "Android"
    : /Mac OS X/.test(ua) ? "macOS"
    : /Linux/.test(ua) ? "Linux"
    : null
  return os ? `${browser} · ${os}` : browser
}

const ago = (value, now) => {
  if (!value) return "-"
  const minutes = Math.floor((now - new Date(value).getTime()) / 60000)
  if (minutes < 1) return "Az önce"
  return `${minutes} dk önce`
}

const timeOf = (value) =>
  value ? new Date(value).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : ""

const DeviceBadge = ({ type }) => (
  <span
    className="badge border-0"
    style={{ borderRadius: 999, fontWeight: 600, color: "#fff", background: type === "MOBILE" ? ORANGE : NAVY }}
  >
    <i className={`bx ${type === "MOBILE" ? "bx-mobile" : "bx-desktop"} me-1`} />
    {type === "MOBILE" ? "Mobil" : "Tarayıcı"}
  </span>
)

const OnlineUsers = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState("")
  const [device, setDevice] = useState("ALL")
  const [loadedAt, setLoadedAt] = useState(Date.now())

  document.title = "Çevrimiçi Kullanıcılar | Tahsilat360"

  const load = useCallback(async () => {
    try {
      setError(null)
      const res = await getOnlineUsers()
      setUsers(res.data || [])
      setLoadedAt(Date.now())
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR")
    return users.filter((u) => {
      if (device !== "ALL" && u.clientType !== device) return false
      if (!q) return true
      return [u.username, u.email, u.companyName, u.companyCode, u.accountName, u.accountCode]
        .some((v) => v && v.toLocaleLowerCase("tr-TR").includes(q))
    })
  }, [users, search, device])

  const mobileCount = users.filter((u) => u.clientType === "MOBILE").length
  const uniqueCount = new Set(users.map((u) => u.userId)).size

  const styles = `
    ${getModernTableStyles("online-users")}
    .online-users .stat { background: #F7F9FC; border-radius: .75rem; padding: .75rem 1rem; min-width: 140px; }
    .online-users .stat .num { font-size: 1.35rem; font-weight: 700; color: ${NAVY}; }
    .online-users .dot { width: 8px; height: 8px; border-radius: 50%; background: ${GREEN}; display: inline-block; }
  `

  return (
    <div className="page-content online-users">
      <style>{styles}</style>
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h5 className="mb-1 fw-semibold" style={{ color: NAVY }}>Çevrimiçi Kullanıcılar</h5>
            <div className="text-muted" style={{ fontSize: ".82rem" }}>
              Son 5 dakikada panelde ya da mobil uygulamada işlem yapan kullanıcılar. Liste 30 saniyede bir yenilenir.
            </div>
          </div>
          <Button color="light" className="btn-rounded" onClick={load} disabled={loading}>
            <i className="bx bx-refresh me-1" />Yenile
          </Button>
        </div>

        {error && <Alert color="danger">{error}</Alert>}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <div className="stat"><div className="text-muted small">Çevrimiçi kullanıcı</div><div className="num">{uniqueCount}</div></div>
          <div className="stat"><div className="text-muted small">Tarayıcı</div><div className="num">{users.length - mobileCount}</div></div>
          <div className="stat"><div className="text-muted small">Mobil</div><div className="num">{mobileCount}</div></div>
        </div>

        <TableCard>
          <CardBody className="p-4">
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Input
                style={{ maxWidth: 320 }}
                placeholder="Kullanıcı, e-posta, firma ya da cari ara"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Input type="select" style={{ maxWidth: 180 }} value={device} onChange={(e) => setDevice(e.target.value)}>
                <option value="ALL">Tüm cihazlar</option>
                <option value="WEB">Tarayıcı</option>
                <option value="MOBILE">Mobil</option>
              </Input>
              {(search || device !== "ALL") && (
                <Button color="link" className="p-0" onClick={() => { setSearch(""); setDevice("ALL") }}>
                  Filtreyi temizle
                </Button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-muted py-5">
                {users.length === 0 ? "Şu an çevrimiçi kullanıcı yok." : "Filtreye uyan kullanıcı yok."}
              </div>
            ) : (
              <div className="table-responsive rounded-4 border">
                <Table className="table table-hover mb-0">
                  <Thead>
                    <Tr>
                      <Th>Kullanıcı</Th>
                      <Th>Rol</Th>
                      <Th>Firma</Th>
                      <Th>Cari</Th>
                      <Th>Cihaz</Th>
                      <Th className="d-none d-lg-table-cell">Tarayıcı</Th>
                      <Th>Son hareket</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filtered.map((u) => (
                      <Tr key={`${u.userId}-${u.clientType}`} className="border-bottom">
                        <Td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="dot" />
                            <div>
                              <div className="fw-semibold text-dark">{u.username || "-"}</div>
                              <div className="text-muted" style={{ fontSize: ".78rem" }}>{u.email}</div>
                            </div>
                          </div>
                        </Td>
                        <Td>{(u.roles || []).map(translateRole).join(", ") || "-"}</Td>
                        <Td>
                          {u.companyName || "-"}
                          {u.companyCode && <div className="text-muted" style={{ fontSize: ".78rem" }}>{u.companyCode}</div>}
                        </Td>
                        <Td>
                          {u.accountName || "-"}
                          {u.accountCode && <div className="text-muted" style={{ fontSize: ".78rem" }}>{u.accountCode}</div>}
                        </Td>
                        <Td><DeviceBadge type={u.clientType} /></Td>
                        <Td className="d-none d-lg-table-cell" title={u.userAgent || ""}>{browserOf(u.userAgent, u.clientType)}</Td>
                        <Td>
                          {ago(u.lastSeenAt, loadedAt)}
                          <div className="text-muted" style={{ fontSize: ".78rem" }}>{timeOf(u.lastSeenAt)}</div>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </TableCard>
      </div>
    </div>
  )
}

export default OnlineUsers
